import React, { useState, useEffect } from 'react'
import { T, BottomNav, setBookAffiliates } from './ui'
import { getNFLStatus, checkNFLAccess, getNFLAccess, setNFLAccess, getBillingStatus, startNFLCheckout, claimCheckout, getNFLPreview, nflFreeSignup, getNFLFreeToken, getNFLFreeBoard, getNFLReferral, setNFLRef, getNFLBookLinks } from './api'
import { PlayerModal, GameModal, DefenseModal, OpsSheet } from './modals'
import HomeTab from './tabs/HomeTab'
import GridsTab from './tabs/GridsTab'   // Players hub: Rankings | Stats | Defense
import GamesTab from './tabs/GamesTab'
import GameTicker from './GameTicker'
import RecordTab from './tabs/RecordTab'
import MoreTab from './tabs/MoreTab'      // Futures · Fantasy · Trends · Sim Check
import TouchdownsTab from './tabs/TouchdownsTab'  // Touchdowns hub: Pay Dirt · Most likely · Matchups · Due
import LadderTab from './tabs/LadderTab'    // Ladder Spotter — promoted to a full view (not just under More)
import MatchupSheetsTab from './tabs/MatchupSheetsTab'  // Matchup Sheets — soft defenses + who's facing them
import BlowoutTab from './tabs/BlowoutTab'  // Blowout Spots — big favorites + the backup RB angle
import CommunityTab from './tabs/CommunityTab'  // Community — Wall (public picks) · Leaderboard · Requests roadmap
import PlayLab from './tabs/StudioTab'   // Play Lab — generate a ranked play list (+ share cards); was Card Studio
import GlossarySheet from './glossary'
import { track, setTrackWeek } from './track'
import { ParlaySheet, SlipButton, legKey } from './parlay'
import { shareNflTop10, shareNflMarket, shareNflGameplan, shareNflSpots, shareNflRecap, shareNflThursday, shareNflPaydirt, shareNflFavorites, shareNflDue, shareNflSheets, shareNflDefenseCard } from './share'
import { playText, teamNick } from './plain'
import { OddsRangeButton, OddsRangeBar } from './oddsfilter'

// Post cards — the shareable ranked-slate graphics (the viral loop). Opens the native
// share sheet on mobile, falls back to a PNG download on desktop.
function CardsSheet({ season, week, onClose }) {
  const [busy, setBusy] = useState('')
  const run = (key, fn) => async () => { if (busy) return; setBusy(key); try { await fn() } catch (_) {} setBusy('') }
  const cards = [
    ['thu', '🌙 Thursday Night', "Tonight's game — the model's top plays", () => shareNflThursday(season, week)],
    ['favorites', '⭐ Favorites', 'Top plays ranked · tagged MATCHUP vs VOLUME', () => shareNflFavorites(season, week)],
    ['top10', '🏈 Top 10 Board', 'Anytime TD · Rec Yds · Rush Yds · Receptions', () => shareNflTop10(season, week)],
    ['td', '🎯 Anytime TD board', 'The top scorers, model-ranked', () => shareNflMarket('anytime_td', season, week)],
    ['plan', '📋 Game Plan', "This week's skill plays by conviction", () => shareNflGameplan(season, week)],
    ['paydirt', '🏈 Pay Dirt', 'Best red-zone TD setups · SETUP + chance to score', () => shareNflPaydirt(season, week)],
    ['due', '🔥 Due', 'Cold but still projected to score — regression value', () => shareNflDue(season, week)],
    ['sheets', '🔥 Matchup Sheets', 'Soft defenses by position + who faces them', () => shareNflSheets(season, week)],
    ['defense', '🛡️ Softest Defenses', 'Who to attack this week, by position', () => shareNflDefenseCard(season, week)],
    ['spots', '📈 Matchup Spots', 'Where the model sees the yards', () => shareNflSpots(season, week)],
    ['recap', '📊 Week Recap', 'Graded results — what cashed & what missed', () => shareNflRecap(season, week)],
  ]
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.bg, width: '100%', maxWidth: 460, borderRadius: '18px 18px 0 0', border: `1px solid ${T.border}`, padding: '18px 16px 28px' }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: T.border2, margin: '0 auto 14px' }} />
        <div style={{ fontSize: 17, fontWeight: 900, color: T.text, marginBottom: 3 }}>Share a board</div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 14 }}>Model-ranked graphics for this week — post to X, IG or the group chat.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {cards.map(([k, title, sub, fn]) => (
            <button key={k} onClick={run(k, fn)} disabled={!!busy} style={{
              textAlign: 'left', cursor: busy ? 'default' : 'pointer', background: T.card,
              border: `1px solid ${T.border2}`, borderRadius: 12, padding: '13px 14px', color: T.text, opacity: busy && busy !== k ? 0.5 : 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800 }}>{busy === k ? 'Building…' : title}</div>
              <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>{sub}</div>
            </button>
          ))}
        </div>
        <div style={{ fontSize: 10, color: T.faint, textAlign: 'center', marginTop: 14 }}>Research, not advice · 21+</div>
      </div>
    </div>
  )
}

// Current NFL season — overridable per-deploy without a code change (bump each
// year, or set VITE_NFL_SEASON). Must match the backend's CURRENT_NFL_SEASON.
const SEASON = Number(import.meta.env.VITE_NFL_SEASON) || 2026

/**
 * Access lock — soft-launch gate. The NFL board reads are code-gated server-side;
 * an un-coded visitor must be able to ENTER their invite code here (otherwise the
 * app silently falls back to sample data, which looks real but isn't). Stores the
 * code in the shared 'brkr_access' slot, then re-probes the gate.
 */
function NFLLock({ mode = 'locked', onUnlock, onFree }) {
  const upgrade = mode === 'upgrade'
  const [code, setCode] = useState(getNFLAccess())
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [billing, setBilling] = useState(null)
  const [peek, setPeek] = useState(null)
  const [email, setEmail] = useState('')
  // Referral code — prefilled if they arrived via a ?ref= link, or typed by hand (e.g. CJ01).
  const [refCode, setRefCode] = useState(() => { try { return localStorage.getItem('brkr_ref') || '' } catch (_) { return '' } })
  const [showCode, setShowCode] = useState(mode === 'upgrade')   // code box hidden for new visitors
  const startFree = async () => {
    if (!email.trim() || busy) return
    setBusy(true); setErr('')
    try { await nflFreeSignup(email.trim(), refCode.trim()); setBusy(false); onFree && onFree() }
    catch (e) { setBusy(false); setErr(e.message || 'Could not sign up. Try again.') }
  }
  useEffect(() => { let a = true; getBillingStatus().then(b => { if (a) setBilling(b) })
    getNFLPreview(SEASON).then(d => { if (a && d && !d._sample) setPeek(d) }); return () => { a = false } }, [])
  const submit = async () => {
    if (!code.trim()) return
    setBusy(true); setErr('')
    setNFLAccess(code)
    const state = await checkNFLAccess(SEASON)
    setBusy(false)
    if (state === 'open') onUnlock()
    else if (state === 'upgrade') setErr('That code doesn’t include NFL. Enter an NFL or all-access code.')
    else setErr('That code isn’t active. Check it and try again.')
  }
  const buy = async (plan) => {
    setBusy(true); setErr('')
    try { if (refCode.trim()) setNFLRef(refCode.trim())   // a typed code rides into checkout too
      window.location.href = await startNFLCheckout(plan) }
    catch (e) { setBusy(false); setErr(e.message || 'Could not start checkout.') }
  }
  const canBuy = billing && billing.secret_key_set && billing.price_all_set
  return (
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 24, fontFamily: "-apple-system,'SF Pro Display',Arial,sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'center', marginBottom: 6 }}>
          <div style={{ fontWeight: 900, fontSize: 30, letterSpacing: '-0.04em' }}>
            <span style={{ color: T.text }}>LINE</span><span style={{ color: T.green }}>BRKR</span>
          </div>
          <span style={{ fontWeight: 800, fontSize: 12, color: '#04140D', background: T.green,
            borderRadius: 5, padding: '2px 6px', letterSpacing: '0.06em' }}>NFL</span>
        </div>
        <div style={{ color: T.muted, fontSize: 13, marginBottom: 12 }}>
          {upgrade ? 'Your plan is MLB-only — add NFL with an NFL or all-access code'
                   : '3 days free, then $10/mo — full access, locked for life · cancel anytime'}</div>
        {/* Proof badge — the 2-second credibility hit. Links to the public settled record
            (CLV + calibration), the one claim a self-reported-stats competitor can't match. */}
        <a href="/proof" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none',
          background: T.greenDim, border: `1px solid ${T.greenMid}`, borderRadius: 999, padding: '6px 13px',
          marginBottom: peek && peek.top ? 16 : 20 }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: T.green, letterSpacing: '0.02em' }}>✓ CLV-proven · Calibrated</span>
          {peek?.top_rated_hit ? <span style={{ fontSize: 11, fontWeight: 800, color: T.text, fontFamily: T.mono }}>{peek.top_rated_hit}%</span> : null}
          <span style={{ fontSize: 10.5, color: T.muted }}>see the record →</span>
        </a>

        {/* Free peek — a taste of this week's board so a cold visitor sees the value
            before the paywall. One top play + counts, not the whole board. */}
        {peek && peek.top && (
          <div style={{ background: T.card, border: `1px solid ${T.greenMid}`, borderRadius: 12, padding: '12px 14px', marginBottom: 18, textAlign: 'left' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.green, letterSpacing: '0.08em', marginBottom: 7 }}>★ THIS WEEK'S TOP PLAY</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{peek.top.name}</div>
                <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>{peek.top.pos} · {teamNick(peek.top.team)} vs {teamNick(peek.top.opp)} · <b style={{ color: T.text }}>{peek.top.is_td ? 'Anytime TD' : `${playText(peek.top.market, peek.top.line)}`}</b></div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 20, lineHeight: 1, color: T.green }}>{peek.top.prob}%</div>
                <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.04em' }}>{peek.top.is_td ? 'TO SCORE' : 'TO HIT'}</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 10, paddingTop: 9, borderTop: `1px solid ${T.border}` }}>
              <b style={{ color: T.text }}>{peek.strong || peek.plays}</b> more plays across <b style={{ color: T.text }}>{peek.games}</b> games{peek.top_rated_hit ? <> · top-rated plays hit <b style={{ color: T.green }}>{peek.top_rated_hit}%</b></> : ''} — unlock below.
            </div>
          </div>
        )}

        {/* PRIMARY: start the free trial (the main conversion path, leads the screen) */}
        {canBuy && (
          <div style={{ marginTop: peek && peek.top ? 2 : 8 }}>
            <button onClick={() => buy('all')} disabled={busy} style={{ width: '100%', padding: '14px 0', fontSize: 16, fontWeight: 800,
              background: T.green, color: '#04140D', border: 'none', borderRadius: 10, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}>
              {busy ? '…' : 'Start 3-day free trial'}
            </button>
            <div style={{ fontSize: 10.5, color: T.faint, marginTop: 7, lineHeight: 1.4, textAlign: 'center' }}>
              Then <b style={{ color: T.muted }}>$10/mo — locked for life</b>. All sports, every feature. Cancel anytime.
            </div>
          </div>
        )}

        {/* Referral code — always visible (not only in the free door) so any user, on their way
            to the trial OR the free board, can enter a friend's code. Prefilled from a ?ref= link;
            rides the shared brkr_ref slot into both the free signup and the paid checkout. */}
        <input value={refCode} onChange={e => setRefCode(e.target.value)} placeholder="Referral code (optional)"
          autoCapitalize="characters" autoCorrect="off" spellCheck={false}
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 12, padding: '10px 14px', fontSize: 13,
            textAlign: 'center', background: T.card, color: T.text, letterSpacing: '0.04em',
            border: `1px solid ${refCode.trim() ? T.greenMid : T.border}`, borderRadius: 10, outline: 'none' }} />

        {/* SECONDARY: free email door (no card). Hidden on the MLB-only upgrade path. */}
        {!upgrade && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 11, color: T.muted, letterSpacing: '0.06em', marginBottom: 10 }}>— OR GET 5 FREE PLAYS —</div>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && startFree()} placeholder="you@email.com"
              style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 15,
                textAlign: 'center', background: T.card, color: T.text,
                border: `1px solid ${T.border}`, borderRadius: 10, outline: 'none' }} />
            <button onClick={startFree} disabled={busy || !email.trim()}
              style={{ width: '100%', marginTop: 8, padding: '12px 0', fontSize: 14, fontWeight: 800,
                background: T.card, color: T.green, border: `1px solid ${T.green}`, borderRadius: 10,
                cursor: (busy || !email.trim()) ? 'default' : 'pointer', opacity: (busy || !email.trim()) ? 0.55 : 1 }}>
              {busy ? '…' : 'Get 5 free plays'}
            </button>
            <div style={{ fontSize: 10.5, color: T.faint, marginTop: 6, lineHeight: 1.4, textAlign: 'center' }}>
              The model's top play in each market, updated daily. No card, no code.
            </div>
          </div>
        )}

        {/* Public proof — a cold visitor can vet the track record before signing up */}
        <a href="/proof" style={{ display: 'block', marginTop: 16, fontSize: 13, fontWeight: 700,
          color: T.green, textDecoration: 'none' }}>📊 See our public track record →</a>

        {/* Existing users only — the invite code, tucked at the bottom behind a small link so a
            new visitor never lands on a field they don't have. */}
        <div style={{ marginTop: 16 }}>
          {!showCode ? (
            <button onClick={() => setShowCode(true)} style={{ background: 'none', border: 'none', cursor: 'pointer',
              color: T.muted, fontSize: 12.5, fontWeight: 700, textDecoration: 'underline', padding: 0 }}>
              Have an invite code?
            </button>
          ) : (
            <>
              <input value={code} onChange={e => setCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submit()} placeholder="Access code" autoFocus
                style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 15,
                  textAlign: 'center', letterSpacing: '0.12em', textTransform: 'uppercase',
                  background: T.card, color: T.text, border: `1px solid ${err ? T.red : T.border}`,
                  borderRadius: 10, outline: 'none', fontFamily: T.mono }} />
              {err && <div style={{ color: T.red, fontSize: 12, marginTop: 8 }}>{err}</div>}
              <button onClick={submit} disabled={busy || !code.trim()}
                style={{ width: '100%', marginTop: 10, padding: '12px 0', fontSize: 14, fontWeight: 800,
                  background: T.card, color: T.text, border: `1px solid ${T.border2}`, borderRadius: 10,
                  cursor: (busy || !code.trim()) ? 'default' : 'pointer', opacity: (busy || !code.trim()) ? 0.55 : 1 }}>
                {busy ? 'Checking…' : 'Enter'}
              </button>
            </>
          )}
        </div>

        <div style={{ marginTop: 16, fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
          <b style={{ color: T.text }}>21+</b> · Research tool, not betting advice · not a sportsbook.
          <br />Bet responsibly. Problem gambling? Call <b style={{ color: T.text }}>1-800-GAMBLER</b>.
        </div>
      </div>
    </div>
  )
}

// One compact free-tier play row — a taste, not the full card (no edge/book/line-shopping).
function FreeRow({ p }) {
  const isTD = p.market === 'Anytime TD'
  const pct = Math.round((p.prob || 0) * 100)
  const c = pct >= 58 ? T.green : pct >= 50 ? T.text : T.muted
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: T.card,
      border: `1px solid ${T.border}`, borderRadius: 10, padding: '11px 12px', marginBottom: 8 }}>
      {p.headshot ? <img src={p.headshot} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} /> : null}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
        <div style={{ fontSize: 11, color: T.muted }}>
          {[p.pos, p.team, p.opp ? `vs ${teamNick(p.opp)}` : ''].filter(Boolean).join(' · ')} · {isTD ? 'Anytime TD' : `${playText(p.market, p.line)}`}
        </div>
      </div>
      {p.tier ? <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.03em', color: T.muted,
        border: `1px solid ${T.border}`, borderRadius: 5, padding: '2px 5px', whiteSpace: 'nowrap' }}>{p.tier}</span> : null}
      <span style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 16, color: c, minWidth: 44, textAlign: 'right' }}>{pct}%</span>
    </div>
  )
}

// Refer & Earn sheet — a bottom-sheet overlay wrapping the ReferCard, opened from the
// header gift icon or the More tab, so EVERY user (free or paid) can find their code
// without hunting. Tap outside or ✕ to close.
function ReferSheet({ onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, background: T.bg,
        borderTop: `1px solid ${T.border}`, borderRadius: '16px 16px 0 0', padding: '14px 16px 30px',
        maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <div style={{ fontSize: 17, fontWeight: 900 }}>🎁 Refer &amp; Earn</div>
          <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 8,
            background: T.card, border: `1px solid ${T.border2}`, color: T.muted, fontSize: 14, cursor: 'pointer' }}>✕</button>
        </div>
        <ReferCard embedded />
        <div style={{ fontSize: 10.5, color: T.faint, marginTop: 14, textAlign: 'center', lineHeight: 1.5 }}>
          Share your link. When 5 friends join and stay a paid month, you earn $20 — every 5. Works for any sport.
        </div>
      </div>
    </div>
  )
}

// Refer-a-friend card — shows the user their share link + how many they've brought in.
// "Track only" reward model: attribution is recorded; the owner comps rewards by hand.
// `embedded` renders a loading/empty state (for the Refer & Earn sheet) instead of hiding.
function ReferCard({ embedded }) {
  const [r, setR] = useState(null)
  const [copied, setCopied] = useState(false)
  useEffect(() => { let a = true; getNFLReferral().then(d => { if (a && d && !d._sample) setR(d) }); return () => { a = false } }, [])
  if (!r || !r.ref_code) return embedded
    ? <div style={{ marginTop: 14, background: T.card, border: `1px solid ${T.border2}`, borderRadius: 12, padding: 16, textAlign: 'center', color: T.muted, fontSize: 12.5, lineHeight: 1.5 }}>
        {r ? 'Your referral link will appear here once your account is set up.' : 'Loading your referral link…'}
      </div>
    : null
  const link = `${window.location.origin}/?ref=${r.ref_code}`
  const every = r.reward_every || 5
  const per = r.reward_per || 20
  const qualified = r.qualified || 0
  const earned = r.earned || 0
  const toNext = r.to_next || every
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1600) } catch (_) {}
  }
  return (
    <div style={{ marginTop: 16, background: T.card, border: `1px solid ${T.border2}`, borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 3 }}>Refer friends → earn ${per} 🎁</div>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 10, lineHeight: 1.45 }}>
        Every <b style={{ color: T.text }}>{every}</b> friends who join and stay a paid month = <b style={{ color: T.green }}>${per}</b>.
      </div>
      {/* Reward progress — qualified (past a paid month), earned so far, and how many more to the next $20 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.green, lineHeight: 1 }}>{qualified}</div>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 3, letterSpacing: '.03em' }}>QUALIFIED</div>
        </div>
        <div style={{ flex: 1, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: earned > 0 ? T.green : T.text, lineHeight: 1, fontFamily: T.mono }}>${earned}</div>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 3, letterSpacing: '.03em' }}>EARNED</div>
        </div>
        <div style={{ flex: 1, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.text, lineHeight: 1 }}>{toNext}</div>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 3, letterSpacing: '.03em' }}>TO NEXT ${per}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly value={link} onFocus={e => e.target.select()} style={{ flex: 1, minWidth: 0, boxSizing: 'border-box',
          background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9, padding: '10px 12px', color: T.muted, fontSize: 12 }} />
        <button onClick={copy} style={{ flexShrink: 0, padding: '0 14px', borderRadius: 9, cursor: 'pointer',
          fontSize: 13, fontWeight: 800, border: 'none', background: T.green, color: '#04140D' }}>{copied ? '✓' : 'Copy'}</button>
      </div>
      <div style={{ fontSize: 10.5, color: T.faint, marginTop: 8, lineHeight: 1.4 }}>
        {r.referred} signed up from your link so far. Rewards paid by hand once a friend completes a paid month.
      </div>
    </div>
  )
}

// Free board — what an email-signup (no code) user sees: the model's top play in each
// market, plus an upsell to unlock the full board with a code. Fully separate from the
// gated board; reads /nfl/free/board with the stored free token.
function FreeBoard({ onEnterCode }) {
  const [d, setD] = useState(null)
  useEffect(() => { let a = true; getNFLFreeBoard(SEASON).then(r => { if (a) setD(r) }); return () => { a = false } }, [])
  const plays = d?.plays || []
  return (
    <div style={{ background: T.bg, minHeight: '100vh', maxWidth: 460, margin: '0 auto', color: T.text,
      fontFamily: "-apple-system,'SF Pro Display',Arial,sans-serif", paddingBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px',
        borderBottom: `1px solid ${T.border}`, background: '#0D1117' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <div style={{ fontWeight: 900, fontSize: 22, letterSpacing: '-0.04em' }}><span style={{ color: T.text }}>LINE</span><span style={{ color: T.green }}>BRKR</span></div>
          <span style={{ fontWeight: 800, fontSize: 11, color: '#04140D', background: T.green, borderRadius: 5, padding: '2px 6px' }}>NFL</span>
          <span style={{ fontWeight: 800, fontSize: 10, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 5, padding: '2px 6px' }}>FREE</span>
        </div>
        <a href="/" style={{ fontSize: 12.5, fontWeight: 700, color: T.muted, textDecoration: 'none' }}>Sports →</a>
      </div>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 3 }}>Your free plays</div>
        <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 14, lineHeight: 1.45 }}>
          The model's top play in each market this week — a taste of the board. Unlock every ranked play, edges &amp; line-shopping with an access code.
        </div>
        {d == null && <div style={{ color: T.muted, fontSize: 13, padding: '30px 0', textAlign: 'center' }}>Loading…</div>}
        {d != null && plays.length === 0 && (
          <div style={{ color: T.muted, fontSize: 13, padding: '20px 0' }}>No plays posted yet this week — check back closer to game day.</div>
        )}
        {plays.map((p, i) => <FreeRow key={i} p={p} />)}
        <div style={{ marginTop: 18, background: T.card, border: `1px solid ${T.greenMid}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>Want the full board?</div>
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 11, lineHeight: 1.45 }}>
            Every market ranked top-to-bottom, model edges vs the book, line-shopping, live in-game tracking and the full track record.
          </div>
          <button onClick={onEnterCode} style={{ width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 800,
            background: T.green, color: '#04140D', border: 'none', borderRadius: 10, cursor: 'pointer' }}>Enter access code</button>
          <a href="/proof" style={{ display: 'block', marginTop: 10, textAlign: 'center', fontSize: 12.5, fontWeight: 700, color: T.green, textDecoration: 'none' }}>📊 See the public track record →</a>
        </div>
        <ReferCard />
        <div style={{ marginTop: 16, fontSize: 10.5, color: T.faint, textAlign: 'center', lineHeight: 1.5 }}>Model probabilities · research, not advice · 21+</div>
      </div>
    </div>
  )
}

/**
 * NFL app — same layout/chrome as the MLB UnifiedApp (top nav, bottom nav, dark
 * theme, card styles), fully isolated. Reachable at ?sport=nfl. The only NFL
 * twist is the weekly cadence: a Week selector replaces the MLB date picker.
 */
export default function NFLApp(){
  const [view,setView] = useState('home')
  const [week,setWeek] = useState(1)
  // Deep-link a specific market into the Players → Rankings tab (e.g. tapping a
  // "Passing" row on Home opens passing rankings, not the default touchdowns).
  const [playersMarket,setPlayersMarket] = useState(null)
  // The four TD screens now live under one Touchdowns tab; old destinations (Pay Dirt / TD Lab,
  // TD Scorers, TD Matchups, Due) still work and open the matching view inside it.
  const [tdView,setTdView] = useState('paydirt')
  const [tdKey,setTdKey] = useState(0)           // bump to re-open Touchdowns on a deep-linked view
  const [homeLens,setHomeLens] = useState(null)  // {lens, n}: open This Week on Most likely / Unders
  const [moreView,setMoreView] = useState(null)   // tool to open inside More (My Board shortcuts)
  const TD_ALIAS = { touchdowns: null, tdlab: 'paydirt', paydirt: 'paydirt', scorers: 'likely', tdroles: 'matchups', due: 'due', valuetds: 'value' }
  const goView = v => {                                                    // plain nav — clears any market
    setPlayersMarket(null)
    if (v in TD_ALIAS) { if (TD_ALIAS[v]) { setTdView(TD_ALIAS[v]); setTdKey(k => k + 1) } return setView('touchdowns') }
    if (v === 'more') setMoreView(null)
    if (v === 'studio') v = 'playlab'          // old name for Play Lab
    setView(v)
  }
  // Jump into Play Lab with a game preselected (the Games-tab "Generate for this game" shortcut).
  const openStudioForGame = g => { setPlayersMarket(null); setStudioGame(g?.game_id || null); setView('playlab') }
  const openMarket = mk => { setPlayersMarket(mk); setView('players') }   // market-specific nav
  const [attackInitial, setAttackInitial] = useState(null)   // deep-link into Games → Attack sub
  // My Board shortcut tiles jump straight to a destination the user pinned (Pay Dirt, Defense…).
  const onNavigate = (dest) => {
    if (!dest) return
    if (dest.startsWith('mkt:')) return openMarket(dest.slice(4))
    if (dest === 'defense') { setAttackInitial('defense'); return goView('games') }
    if (dest === 'glossary') return setGlossary(true)
    if (dest.startsWith('home:')) { setHomeLens({ lens: dest.slice(5), n: Date.now() }); return goView('home') }
    if (dest.startsWith('more:')) { setMoreView(dest.slice(5)); setPlayersMarket(null); return setView('more') }
    goView(dest)   // any view id, incl. the old TD ids (aliased into Touchdowns) and more:<tool>
  }
  // Owner Ops is hidden from normal users — it's pipeline plumbing, not a feature. It
  // appears only if this device has already acted as owner (entered an ops code) or the
  // owner reveals it once via ?admin (persisted), mirroring the MLB app.
  const [showOps] = useState(() => {
    try {
      if (localStorage.getItem('brkr_ops') || localStorage.getItem('brkr_admin') || localStorage.getItem('brkr_admin_reveal')) return true
      if (/(?:^|[?&#])admin\b/i.test(window.location.search + window.location.hash)) {
        try { localStorage.setItem('brkr_admin_reveal','1') } catch(_){}
        return true
      }
    } catch(_){}
    return false
  })
  // Simple / Pro tier — Simple keeps the app clean for a casual user; Pro reveals the
  // advanced columns (NGS/EPA) and deeper detail. Persisted per viewer.
  const [pro,setPro] = useState(() => { try { return localStorage.getItem('brkr_nfl_pro')==='1' } catch(_){ return false } })
  useEffect(() => { try { localStorage.setItem('brkr_nfl_pro', pro?'1':'0') } catch(_){} }, [pro])
  const [player,setPlayer] = useState(null)   // open PlayerModal
  const [game,setGame] = useState(null)       // open GameModal
  const [gameFilter,setGameFilter] = useState(null)  // global quick filter: null=all, else {game_id,teams,label}
  const [defense,setDefense] = useState(null) // open DefenseModal (opp drill-down)
  const [ops,setOps] = useState(false)        // open owner Ops panel
  const [refer,setRefer] = useState(false)    // open Refer & Earn sheet
  const [cards,setCards] = useState(false)     // open the share-a-board sheet
  const [glossary,setGlossary] = useState(false) // open the plain-English glossary
  const [studioGame,setStudioGame] = useState(null) // Play Lab preselected game (from Games tab)
  const [gate,setGate] = useState('checking') // checking | locked | open
  const [weekReady,setWeekReady] = useState(false)   // current week resolved (board waits on it)

  // Mixed-market parlay slip — shared across tabs, persisted so it survives a
  // reload or slate change. A leg is {gsis_id, prop, market, name, team, opp, pos}.
  const [slip,setSlip] = useState(() => { try { return JSON.parse(localStorage.getItem('brkr_nfl_slip')||'[]') } catch(_) { return [] } })
  const [slipOpen,setSlipOpen] = useState(false)
  useEffect(() => { try { localStorage.setItem('brkr_nfl_slip', JSON.stringify(slip)) } catch(_) {} }, [slip])
  // Load affiliate book links once → "open the book" taps become tracked revenue links.
  useEffect(() => { getNFLBookLinks().then(setBookAffiliates).catch(() => {}) }, [])
  const addLeg = leg => { if(!leg||!leg.gsis_id||!leg.prop) return
    setSlip(s => s.some(x => legKey(x)===legKey(leg)) ? s : [...s, leg]); setSlipOpen(true) }
  const removeLeg = leg => setSlip(s => s.filter(x => legKey(x)!==legKey(leg)))
  const clearSlip = () => { setSlip([]); setSlipOpen(false) }
  const slipKeys = new Set(slip.map(legKey))

  // Probe the access gate before rendering the board. 'open' = valid code or no
  // gate; 'locked' = show the code entry. The board is locked to the CURRENT NFL
  // week (season-long stuff lives in the Futures tab), so anchor to current_week —
  // falling back to the latest week that actually has projections.
  useEffect(() => {
    let live = true
    const probe = () => checkNFLAccess(SEASON).then(state => {
      if (!live) return
      // A returning free-tier user (no code, but an email token stored) lands on the free
      // board rather than the lock screen.
      if (state === 'locked' && getNFLFreeToken()) { setGate('free'); return }
      setGate(state)
    })
    // Returning from Stripe checkout: ?session_id=… → claim the code, then probe.
    const sid = new URLSearchParams(window.location.search).get('session_id')
    if (sid) {
      claimCheckout(sid).then(res => {
        if (res?.code) {
          setNFLAccess(res.code)
          // Surface the code once via the shared reveal screen (App.jsx), so a buyer
          // can save it and log in on other devices even before email is configured.
          try { localStorage.setItem('brkr_new_code', res.code) } catch (_) {}
          window.location.href = '/?sport=nfl'
          return
        }
        try { window.history.replaceState({}, '', '/?sport=nfl') } catch (_) {}
        probe()
      })
    } else {
      probe()
    }
    return () => { live = false }
  }, [])

  // Resolve the CURRENT week whenever the board opens — on first load AND after a code
  // unlock (the unlock path used to skip this and land paying users on Week 1). The board
  // waits on it, so nothing loads a Week-1 slate first and then reloads Week N.
  useEffect(() => {
    if (gate !== 'open' || weekReady) return
    let live = true
    getNFLStatus(SEASON).then(d => {
      if (!live) return
      const wks = (d?.projections?.weeks || []).filter(w => w >= 1 && w <= 18)
      const cw = d?.current_week
      setWeek((cw >= 1 && cw <= 18) ? cw : (wks.length ? Math.max(...wks) : 1))
      setWeekReady(true)
    }).catch(() => { if (live) setWeekReady(true) })
    return () => { live = false }
  }, [gate, weekReady])

  // Anonymous usage (track.js): screens, player cards and game breakdowns people open.
  useEffect(() => { setTrackWeek(week) }, [week])
  useEffect(() => { if (gate === 'open' && weekReady) track('view', view) }, [view, gate, weekReady])
  useEffect(() => { if (player) track('player_open', player.market || player.prop_type || '') }, [player])
  useEffect(() => { if (game) track('game_open', `${game.away?.abbr || ''}@${game.home?.abbr || ''}`) }, [game])

  if (gate === 'checking' || (gate === 'open' && !weekReady)) return (
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 16, fontFamily: "-apple-system,'SF Pro Display',Arial,sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontWeight: 900, fontSize: 26, letterSpacing: '-0.04em' }}><span style={{ color: T.text }}>LINE</span><span style={{ color: T.green }}>BRKR</span></div>
        <span style={{ fontWeight: 800, fontSize: 11, color: '#04140D', background: T.green, borderRadius: 5, padding: '2px 6px', letterSpacing: '0.06em' }}>NFL</span>
      </div>
      <div style={{ width: 26, height: 26, borderRadius: '50%', border: `3px solid ${T.border}`, borderTopColor: T.green, animation: 'spin 0.8s linear infinite' }} />
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  )
  if (gate === 'free') return <FreeBoard onEnterCode={() => setGate('locked')} />
  if (gate === 'locked' || gate === 'upgrade') return <NFLLock mode={gate} onUnlock={() => setGate('open')} onFree={() => setGate('free')} />

  return (
    <div style={{background:T.bg,minHeight:"100vh",maxWidth:460,margin:"0 auto",fontFamily:"-apple-system,'SF Pro Display',Arial,sans-serif",color:T.text,paddingBottom:64}}>
      {/* Top nav — matches MLB, with an NFL badge + back-to-MLB link */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderBottom:`1px solid ${T.border}`,background:"#0D1117",position:"sticky",top:0,zIndex:10}}>
        <div style={{display:"flex",alignItems:"baseline",gap:8}}>
          <div style={{fontWeight:900,fontSize:22,letterSpacing:"-0.04em"}}><span style={{color:T.text}}>LINE</span><span style={{color:T.green}}>BRKR</span></div>
          <span style={{fontWeight:800,fontSize:11,color:"#04140D",background:T.green,borderRadius:5,padding:"2px 6px",letterSpacing:"0.06em"}}>NFL</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:14}}>
          {/* Glossary — plain-English decoder for the ratings & stats. */}
          <button onClick={()=>setGlossary(true)} aria-label="What the numbers mean"
            style={{background:"none",border:`1px solid ${T.border2}`,cursor:"pointer",color:T.muted,
              borderRadius:999,width:22,height:22,fontSize:12,fontWeight:800,lineHeight:1,padding:0,display:"flex",alignItems:"center",justifyContent:"center"}}>?</button>
          {/* Refer & Earn — every user's $20 referral code, one tap from any screen. */}
          <button onClick={()=>setRefer(true)} aria-label="Refer & Earn"
            style={{background:"none",border:"none",cursor:"pointer",color:T.green,padding:0,lineHeight:0,fontSize:17}}>🎁</button>
          {/* Share a board — the viral loop. Model-ranked slate graphics, any user. */}
          <button onClick={()=>setCards(true)} aria-label="Share a board"
            style={{background:"none",border:"none",cursor:"pointer",color:T.muted,padding:0,lineHeight:0}}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
          </button>
          {/* Owner Ops — pipeline plumbing, owner-only (see showOps). Hidden from normal
              users so the only "settings"-looking icon isn't an admin dead-end. */}
          {showOps && (
            <button onClick={()=>setOps(true)} aria-label="Ops"
              style={{background:"none",border:"none",cursor:"pointer",color:T.muted,padding:0,lineHeight:0}}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9c.2.61.76 1.05 1.42 1.09H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
            </button>
          )}
          <a href="?" style={{fontSize:11,color:T.muted,textDecoration:"none"}}>‹ All sports</a>
        </div>
      </div>

      {/* Current-week banner — the board is this week only; season-long futures
          live in the Futures tab. */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 16px",borderBottom:`1px solid ${T.border}`,background:"#0B1018"}}>
        <span style={{fontSize:13,fontWeight:800,color:T.text}}>Week {week}
          <span style={{fontSize:11,fontWeight:600,color:T.muted,marginLeft:6}}>· {SEASON} season</span></span>
        {/* Simple / Pro — shown only on the tabs it actually affects (Games / Players),
            so the header's most prominent control is never inert where users first look. */}
        <div style={{display:"flex",alignItems:"center",gap:6}}>
        {/* Odds range — only on the boards it filters (Players rankings, Touchdowns). */}
        {(view==='players'||view==='touchdowns') && <OddsRangeButton/>}
        {(view==='games'||view==='players'||view==='more') && (
          <div style={{display:"inline-flex",background:T.card2,border:`1px solid ${T.border2}`,borderRadius:999,padding:2,gap:2}}>
            {[["Simple",false],["Pro",true]].map(([lbl,v])=>{
              const on = pro===v
              return <button key={lbl} onClick={()=>setPro(v)} aria-pressed={on} style={{fontSize:10.5,fontWeight:800,
                border:"none",cursor:"pointer",borderRadius:999,padding:"4px 11px",
                color:on?"#04140D":T.muted,background:on?T.green:"transparent"}}>{lbl}</button>
            })}
          </div>
        )}
        </div>
      </div>
      {/* Game ticker — quick-access strip of every game this week (the sportsbook-style top bar).
          Tap a chip to open that matchup's breakdown. Shown on the main views only. */}
      {(view==='home'||view==='games'||view==='players'||view==='touchdowns'||view==='more') && (
        <GameTicker season={SEASON} week={week} filter={gameFilter} onFilter={setGameFilter} />
      )}
      {/* Active-filter bar — immediate feedback that a game is selected (so a tap never feels dead),
          with one tap to open that game's full breakdown or clear the scope. */}
      {gameFilter && (view==='home'||view==='games'||view==='players'||view==='touchdowns'||view==='more') && (
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 16px",background:T.greenDim,
          borderBottom:`1px solid ${T.border}`}}>
          <span style={{fontSize:12,fontWeight:700,color:T.text}}>🎯 Showing <b style={{color:T.green}}>{gameFilter.label}</b> only</span>
          <button onClick={()=>setGame({game_id:gameFilter.game_id,
              away:{abbr:gameFilter.teams[0]},home:{abbr:gameFilter.teams[1]}})}
            style={{marginLeft:"auto",fontSize:11.5,fontWeight:800,color:T.green,background:"transparent",
              border:`1px solid ${T.green}`,borderRadius:8,padding:"5px 10px",cursor:"pointer",whiteSpace:"nowrap"}}>Breakdown ›</button>
          <button onClick={()=>setGameFilter(null)}
            style={{fontSize:11.5,fontWeight:800,color:T.muted,background:"transparent",
              border:`1px solid ${T.border}`,borderRadius:8,padding:"5px 10px",cursor:"pointer",whiteSpace:"nowrap"}}>✕ Clear</button>
        </div>
      )}
      {(view==='players'||view==='touchdowns') && <OddsRangeBar/>}
      {/* One-line explainer — only alongside the toggle */}
      {(view==='games'||view==='players'||view==='more') && (
        <div style={{fontSize:10.5,color:T.muted,padding:"5px 16px 6px",borderBottom:`1px solid ${T.border}`,background:"#0B1018",lineHeight:1.4}}>
          {pro ? <><b style={{color:T.green}}>Pro</b> — showing edges, CLV & advanced stats.</>
               : <><b style={{color:T.text}}>Simple</b> — clean view. Tap <b style={{color:T.green}}>Pro</b> for edges, CLV & advanced detail.</>}
        </div>
      )}

      {view==='home'     && <HomeTab season={SEASON} week={week} setView={goView} onNavigate={onNavigate} onMarket={openMarket} onPlayer={setPlayer} onGame={setGame} onGuide={()=>setGlossary(true)} lensLink={homeLens} gameFilter={gameFilter}/>}
      {view==='games'    && <GamesTab season={SEASON} week={week} onGame={setGame} onPlayer={setPlayer} onMakeCard={openStudioForGame} pro={pro} attackInitial={attackInitial} onAttackConsumed={()=>setAttackInitial(null)}/>}
      {view==='players'  && <GridsTab season={SEASON} week={week} onPlayer={setPlayer} pro={pro} initialMarket={playersMarket} gameFilter={gameFilter}/>}
      {view==='touchdowns' && <TouchdownsTab key={tdKey} season={SEASON} week={week} onPlayer={setPlayer} onAddLeg={addLeg} slipKeys={slipKeys} gameFilter={gameFilter} initial={tdView}/>}
      {(view==='ladder' || view==='sheets' || view==='blowout' || view==='community') && (
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px 4px' }}>
          <button onClick={()=>goView('home')} style={{ background:T.card, border:`1px solid ${T.border}`,
            color:T.green, borderRadius:9, padding:'7px 12px', fontSize:12.5, fontWeight:800, cursor:'pointer' }}>‹ This Week</button>
          <span style={{ fontSize:15, fontWeight:800 }}>{view==='ladder' ? '🪜 Ladder Spotter' : view==='sheets' ? '🔥 Matchup Sheets' : view==='community' ? '🏟️ Community' : '💥 Blowout Spots'}</span>
        </div>
      )}
      {view==='ladder'   && <LadderTab season={SEASON} week={week} onPlayer={setPlayer}/>}
      {view==='sheets'   && <MatchupSheetsTab season={SEASON} week={week} onPlayer={setPlayer}/>}
      {view==='blowout'  && <BlowoutTab season={SEASON} week={week} onPlayer={setPlayer}/>}
      {view==='community'&& <CommunityTab season={SEASON} week={week} onPlayer={setPlayer}/>}
      {view==='record'   && <RecordTab season={SEASON} week={week} onPlayer={setPlayer}/>}
      {view==='playlab'  && <PlayLab season={SEASON} week={week} initialGameId={studioGame} onPlayer={setPlayer} onAddLeg={addLeg} slipKeys={slipKeys}/>}
      {view==='more'     && <MoreTab season={SEASON} week={week} onPlayer={setPlayer} onAddLeg={addLeg} slipKeys={slipKeys} pro={pro} onRefer={()=>setRefer(true)} onNavigate={onNavigate} gameFilter={gameFilter} initial={moreView}/>}

      {player && <PlayerModal e={player} season={SEASON} week={week} pro={pro} onClose={()=>setPlayer(null)}
        onDefense={(opp,pos)=>setDefense({opp,pos})} onAddLeg={addLeg} slipKeys={slipKeys}/>}
      {game && <GameModal game={game} season={SEASON} week={week} onClose={()=>setGame(null)} onPlayer={setPlayer}/>}
      {defense && <DefenseModal opp={defense.opp} pos={defense.pos} season={SEASON} week={week} onClose={()=>setDefense(null)}/>}
      {ops && <OpsSheet season={SEASON} week={week} onClose={()=>setOps(false)}/>}
      {refer && <ReferSheet onClose={()=>setRefer(false)}/>}
      {cards && <CardsSheet season={SEASON} week={week} onClose={()=>setCards(false)}/>}
      {glossary && <GlossarySheet onClose={()=>setGlossary(false)}/>}

      {/* Mixed-market parlay — floating button (any tab) + the correlation slip sheet */}
      {!slipOpen && <SlipButton count={slip.length} onClick={()=>setSlipOpen(true)}/>}
      {slipOpen && <ParlaySheet legs={slip} season={SEASON} week={week}
        onClose={()=>setSlipOpen(false)} onRemove={removeLeg} onClear={clearSlip}/>}

      {/* Responsible-gaming footer — same as MLB */}
      <div style={{padding:"16px 16px 10px",borderTop:`1px solid ${T.border}`,textAlign:"center"}}>
        <div style={{fontSize:11,color:T.muted,lineHeight:1.5}}>
          <b style={{color:T.text}}>21+</b> · Info only, not betting advice · LINEBRKR is a research tool, not a sportsbook.
          <br/>Problem gambling? Call <b style={{color:T.text}}>1-800-GAMBLER</b>.
        </div>
        <a href="?account=1" style={{display:"inline-block",marginTop:10,fontSize:12,fontWeight:700,color:T.muted,textDecoration:"none"}}>Manage subscription →</a>
      </div>

      <BottomNav view={view} setView={goView}/>
    </div>
  )
}
